import { createColumnHelper } from '@tanstack/react-table'
import { FormattedNumber } from 'react-intl'
import { styled } from 'styled-components'

import type { BidPrecision } from '@b-stock/fe-core/bstock-next/hooks'
import {
  CurrencyStringCents,
  FormattedMessage,
} from '@b-stock/fe-core/bstock-react'
import type { ItemDtoQuantityUnitsEnum } from '@b-stock/listing-api-client'

import { YourOfferCell } from '../atoms'
import type { OfferingItemRow } from '../types'
import { LineAmount } from '../values'

const ns = 'Offerings.Details.form'

const columnHelper = createColumnHelper<OfferingItemRow>()

const QuantityUnits = styled.span`
  font-weight: 400;
  letter-spacing: 0.015rem;
  text-transform: none;
`

/**
 * Columns:
 * - "Item Description": item.attributes.description
 * - "UPC", item.attributes.ids.sku
 * - "Qty": item.quantity (item.quantityUnits in header)
 * - "Retail": item.attributes.unitRetail
 * - "Ext. Retail": unitRetail * quantity
 * - "Your Offer", inputs: % of MSRP, quantity
 */
export const getColumns = ({
  bidPrecision,
  currency = 'USD',
  quantityUnits,
}: {
  bidPrecision: BidPrecision
  currency?: string
  quantityUnits: ItemDtoQuantityUnitsEnum
}) => [
  columnHelper.accessor('item.attributes.description', {
    cell: (info) => info.getValue(),
    header: () => <FormattedMessage id={`${ns}.headers.description`} />,
  }),

  columnHelper.accessor((o) => o.item.attributes.ids?.sku, {
    id: 'item.attributes.ids.sku',
    cell: (info) => info.getValue(),
    header: () => <FormattedMessage id={`${ns}.headers.sku`} />,
  }),

  columnHelper.accessor('item.quantity', {
    cell: (info) => <FormattedNumber value={info.getValue()} />,
    header: () => (
      <>
        <FormattedMessage id={`${ns}.headers.quantity`} />{' '}
        <QuantityUnits>
          {'('}
          <FormattedMessage id={`${ns}.quantityUnits.${quantityUnits}`} />
          {')'}
        </QuantityUnits>
      </>
    ),
  }),

  columnHelper.accessor('item.attributes.unitRetail', {
    cell: (info) => (
      <CurrencyStringCents currency={currency} value={info.getValue()} />
    ),
    header: () => <FormattedMessage id={`${ns}.headers.unitRetail`} />,
  }),

  columnHelper.accessor('item.attributes.extRetail', {
    cell: (info) => (
      <CurrencyStringCents currency={currency} value={info.getValue()} />
    ),
    header: () => <FormattedMessage id={`${ns}.headers.extRetail`} />,
  }),

  columnHelper.accessor((o) => o, {
    id: 'yourOffer',
    cell: (info) => (
      <YourOfferCell
        bidPrecision={bidPrecision}
        currency={currency}
        row={info.getValue()}
        rowIndex={info.row.index}
      />
    ),
    header: () => <FormattedMessage id={`${ns}.headers.yourOffer`} />,
    meta: { editable: true, width: { min: 20.5, fixed: 20.5 } },
  }),

  columnHelper.accessor((o) => o, {
    id: 'lineAmount',
    cell: (info) => (
      <LineAmount currency={currency} rowIndex={info.row.index} />
    ),
    header: () => <FormattedMessage id={`${ns}.headers.offerAmount`} />,
  }),
]
